import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OfferCard from '../components/OfferCard';
import OfferDetailView from '../components/OfferDetailView';
import MobileOfferDrawer from '../components/MobileOfferDrawer';
import CompanyCard from '../components/CompanyCard';
import CompanyInsightsModal from '../components/CompanyInsightsModal';
import ErrorBoundary from '../components/ErrorBoundary'; // Import ErrorBoundary
import { Heart, Loader2, Info, X, Check, UserPlus } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { track } from '../lib/analytics';
import PremiumModal from '../components/PremiumModal';
import { useAuth } from '../context/AuthContext';

const FavoritesPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'offres'); // 'offres' or 'entreprises'
    const [isMobile, setIsMobile] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    // Company modal state
    const [selectedCompanyForModal, setSelectedCompanyForModal] = useState(null);
    const [showInsightsModal, setShowInsightsModal] = useState(false);

    // Check for mobile
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 1024);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Check Auth on Mount
    const [showAuthModal, setShowAuthModal] = useState(false);
    useEffect(() => {
        const checkAuth = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                setShowAuthModal(true);
            }
        };
        checkAuth();
    }, []);

    const [favoriteOffers, setFavoriteOffers] = useState([]);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [loadingOffers, setLoadingOffers] = useState(false);

    useEffect(() => {
        const fetchFavoriteOffers = async () => {
            if (activeTab !== 'offres') return;

            setLoadingOffers(true);
            try {
                let favIds = [];

                if (user) {
                    const { data, error } = await supabase
                        .from('user_offer_activities')
                        .select('offer_id')
                        .eq('user_id', user.id)
                        .eq('is_favorite', true);

                    if (error) console.error("Error fetching favorites from DB:", error);
                    if (data) favIds = data.map(item => item.offer_id);
                } else {
                    // Fallback to local storage if not logged in (though auth modal usually blocks this)
                    try {
                        const raw = localStorage.getItem('favorite_offers');
                        if (raw) {
                            const parsed = JSON.parse(raw);
                            if (Array.isArray(parsed)) favIds = parsed;
                        }
                    } catch (e) {
                        console.error("Error parsing favorites:", e);
                        localStorage.setItem('favorite_offers', '[]');
                    }
                }

                if (favIds.length === 0) {
                    setFavoriteOffers([]);
                    return;
                }

                // Split IDs to match column types and avoid Postgres errors
                // Public offers usually have numeric IDs (e.g., 12345)
                // Plus offers usually have UUIDs or string IDs
                const numericIds = favIds.filter(id => !isNaN(Number(id)));
                const uuidIds = favIds.filter(id => isNaN(Number(id))); // Assume non-numeric are for Plus table

                // 1. Fetch Standard Offers (Public) - Only strictly numeric IDs
                let publicData = [];
                if (numericIds.length > 0) {
                    const { data, error } = await supabase
                        .from('offres_vie_bf_public')
                        .select('*')
                        .in('id_offre', numericIds);

                    if (error) console.error("Error fetching public favorites:", error);
                    if (data) publicData = data;
                }

                // 2. Fetch Plus/Exclusive Offers - Fetch valid UUIDs + check numeric ones too just in case schema allows
                // Actually, let's just query Plus with ALL IDs if the column is text/uuid, but if it is uuid type, we must be careful.
                // Safest approach: Query Plus with UUIDs.
                let plusData = [];
                if (uuidIds.length > 0) {
                    const { data, error } = await supabase
                        .from('offres_vie_hors_bf_public')
                        .select('*')
                        .in('id_offre', uuidIds);

                    if (error) console.error("Error fetching plus favorites:", error);
                    if (data) plusData = data;
                }

                // Combine
                const allRawOffers = [...publicData, ...plusData];

                // Country code mapping (expanded)
                const getCountryCode = (name, code) => {
                    // 1. Try DB Code first (Robustness check)
                    if (code && code.length === 2 && code !== 'null') {
                        return code.toUpperCase();
                    }

                    const normalizedName = name ? name.toUpperCase().trim() : '';
                    const map = {
                        'ALLEMAGNE': 'DE', 'BELGIQUE': 'BE', 'ESPAGNE': 'ES', 'ITALIE': 'IT',
                        'PORTUGAL': 'PT', 'SUISSE': 'CH', 'ROYAUME-UNI': 'GB', 'CANADA': 'CA',
                        'ETATS-UNIS': 'US', 'AUSTRALIE': 'AU', 'JAPON': 'JP', 'CHINE': 'CN',
                        'COREE DU SUD': 'KR', 'SINGAPOUR': 'SG', 'EMIRATS ARABES UNIS': 'AE',
                        'MAROC': 'MA', 'SENEGAL': 'SN', 'BRESIL': 'BR', 'MEXIQUE': 'MX',
                        'VIETNAM': 'VN', 'VIET NAM': 'VN', 'INDE': 'IN', 'POLOGNE': 'PL',
                        'ROUMANIE': 'RO', 'PAYS-BAS': 'NL', 'HONGRIE': 'HU',
                        'REPUBLIQUE TCHEQUE': 'CZ', 'TCHEQUIE': 'CZ', 'TCHEQUIE / REPUBLIQUE TCHEQUE': 'CZ',
                        'AUTRICHE': 'AT', 'SUEDE': 'SE', 'NORVEGE': 'NO', 'FINLANDE': 'FI',
                        'DANEMARK': 'DK', 'IRLANDE': 'IE', 'PHILIPPINES': 'PH', 'THAILANDE': 'TH',
                        'INDONESIE': 'ID', 'MALAISIE': 'MY', 'TURQUIE': 'TR', 'GRECE': 'GR',
                        'LUXEMBOURG': 'LU', 'CAMEROUN': 'CM', "COTE D'IVOIRE": 'CI',
                        'LAOS': 'LA', 'EGYPTE': 'EG', 'AFRIQUE DU SUD': 'ZA', 'COLOMBIE': 'CO',
                        'PEROU': 'PE', 'CHILI': 'CL', 'ARGENTINE': 'AR', 'TAIWAN': 'TW',
                        'ARABIE SAOUDITE': 'SA', 'QATAR': 'QA', 'KOWEIT': 'KW', 'LIBAN': 'LB',
                        'JORDANIE': 'JO', 'MAURICE': 'MU', 'KENYA': 'KE', 'MADAGASCAR': 'MG',
                        'CONGO': 'CG', 'REPUBLIQUE DU CONGO': 'CG',
                        'REPUBLIQUE DEMOCRATIQUE DU CONGO': 'CD', 'RDC': 'CD',
                        'ANGOLA': 'AO', 'NIGERIA': 'NG', 'TUNISIE': 'TN', 'AZERBAIDJAN': 'AZ'
                    };

                    return map[normalizedName] || 'FR';
                };

                const mapped = allRawOffers.map(off => ({
                    id: off.id_offre,
                    title: off.intitule_poste || 'Poste sans titre',
                    company: off.entreprise || 'Entreprise inconnue',
                    countryCode: getCountryCode(off.pays, off.country_code),
                    country: off.pays || 'France',
                    city: off.ville || 'TBD',
                    sector: off.parent_label_fr || off.specialization_label_fr || 'Général',
                    badge: off.specialization_label_fr ? 'Expertise' : 'V.I.E',
                    duree: off.duree,
                    duration: off.duree ? `${off.duree} mois` : '12 mois',
                    date: (() => {
                        try {
                            return off.date_publication ? new Date(off.date_publication).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Récemment';
                        } catch (e) { return 'Récemment'; }
                    })(),
                    year: (() => {
                        try {
                            return off.date_publication ? new Date(off.date_publication).getFullYear() : new Date().getFullYear();
                        } catch (e) { return new Date().getFullYear(); }
                    })(),
                    summary: off.sidebar_summary || (off.offre_complete || '').substring(0, 160) + '...',
                    description: off.offre_complete || '',

                    // HTML Content (Handles both schema variants)
                    companyHtml: off.company_html || null,
                    missionHtml: off.mission_html || null,
                    profileHtml: off.profile_html || null,

                    link: off.lien_offre || '#',
                    langues_requises: off.langues_requises || '',
                    isExpired: Boolean(off.url_live) && off.url_live !== 'active',

                    // Standardized Details Object for DetailView
                    details: {
                        entreprise: off.description_entreprise || '',
                        poste: off.offre_complete || '',
                        profil: off.description_candidat || '',
                        langues: off.langues_requises || '',
                    },

                    // Tags for Card
                    tags: [
                        off.parent_label_fr || off.specialization_label_fr,
                        off.duree ? `${off.duree} mois` : null
                    ].filter(Boolean),

                    trackingMode: true
                }));

                setFavoriteOffers(mapped);
                if (mapped.length > 0 && !isMobile) setSelectedOffer(mapped[0]);

            } catch (err) {
                console.error("Error loading favorite offers:", err);
            } finally {
                setLoadingOffers(false);
            }
        };

        fetchFavoriteOffers();
    }, [activeTab, isMobile, user]);

    // Companies Favorites Logic
    const [favoriteCompanies, setFavoriteCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);

    useEffect(() => {
        const fetchFavoriteCompanies = async () => {
            if (activeTab !== 'entreprises') return;

            setLoadingCompanies(true);
            try {
                const favNames = JSON.parse(localStorage.getItem('favorite_companies') || '[]');

                if (favNames.length === 0) {
                    setFavoriteCompanies([]);
                    return;
                }

                // Fetch Stats for ALL companies then filter
                const { data, error } = await supabase.rpc('get_companies_stats');

                if (error) throw error;

                if (data) {
                    const filtered = data.filter(c => favNames.includes(c.name));
                    setFavoriteCompanies(filtered);
                }
            } catch (err) {
                console.error('Error fetching favorite companies:', err);
            } finally {
                setLoadingCompanies(false);
            }
        };

        fetchFavoriteCompanies();
    }, [activeTab]);

    const toggleFavoriteCompany = (name) => {
        // Here, toggling implies Removing because we are in Favorites Page
        const currentFavs = JSON.parse(localStorage.getItem('favorite_companies') || '[]');
        if (currentFavs.includes(name)) {
            const newFavs = currentFavs.filter(n => n !== name);
            localStorage.setItem('favorite_companies', JSON.stringify(newFavs));
            setFavoriteCompanies(prev => prev.filter(c => c.name !== name));
        } else {
            // Re-adding? Rare but possible logic
            const newFavs = [...currentFavs, name];
            localStorage.setItem('favorite_companies', JSON.stringify(newFavs));
        }
    };

    const handleOfferClick = (offer) => {
        track('offer_viewed', { offer_id: offer.id, source: 'favorites', company: offer.company, country: offer.country });
        setSelectedOffer(offer);
        if (isMobile) {
            setMobileDrawerOpen(true);
        }
    };

    const handleRemoveOfferFavorite = async (id) => {
        // Optimistic update
        const newIds = JSON.parse(localStorage.getItem('favorite_offers') || '[]').filter(x => x !== id);
        localStorage.setItem('favorite_offers', JSON.stringify(newIds));
        setFavoriteOffers(prev => prev.filter(o => o.id !== id));

        if (user) {
            try {
                // Determine if we should also keep is_viewed? 
                // Using upsert with false is safer than delete if we assume 'is_viewed' might be true.
                // We need to fetch current state or just blindly upsert?
                // Safest is to just update is_favorite to false.
                // But since we might have is_viewed=true, we should upsert 
                // OR better: use UPDATE which only affects existing rows?
                // If it's in favorites, it MUST exist in user_offer_activities.
                // So UPDATE is appropriate.

                const { error } = await supabase
                    .from('user_offer_activities')
                    .update({ is_favorite: false, updated_at: new Date() })
                    .eq('user_id', user.id)
                    .eq('offer_id', id);

                if (error) console.error("Error removing favorite from DB:", error);
            } catch (err) {
                console.error("Exception removing favorite:", err);
            }
        }
    };

    const [isPremium, setIsPremium] = useState(false);

    useEffect(() => {
        const checkPremium = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data } = await supabase.from('profiles').select('is_premium').eq('id', session.user.id).single();
                setIsPremium(data?.is_premium || false);
            }
        };
        checkPremium();
    }, []);


    // Avatar style generator for companies (same as CompaniesPage)
    const getAvatarStyle = (name) => {
        const colors = [
            { bg: 'bg-blue-100', text: 'text-blue-600' },
            { bg: 'bg-purple-100', text: 'text-purple-600' },
            { bg: 'bg-green-100', text: 'text-green-600' },
            { bg: 'bg-orange-100', text: 'text-orange-600' },
            { bg: 'bg-pink-100', text: 'text-pink-600' },
            { bg: 'bg-indigo-100', text: 'text-indigo-600' },
            { bg: 'bg-teal-100', text: 'text-teal-600' },
            { bg: 'bg-red-100', text: 'text-red-600' }
        ];
        if (!name) return colors[0];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    };

    return (
        <div className="min-h-screen bg-[#F0F9FF] py-6 lg:py-0 px-4 sm:px-6 lg:px-8">
            <Helmet><meta name='robots' content='noindex, nofollow' /></Helmet>
            <ErrorBoundary>
                <div className="max-w-7xl mx-auto">

                    {/* ========== MOBILE HEADER ========== */}
                    <div className="md:hidden flex flex-col items-center pt-0 mb-6">
                        {/* Centered Title */}
                        <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2 mb-4">
                            <Heart className="fill-blue-500 text-blue-500 w-6 h-6" />
                            Mes Favoris
                        </h1>

                        {/* Full-width Tab Selector */}
                        <div className="w-full bg-white p-1 rounded-full shadow-sm border border-gray-200 flex">
                            <button
                                onClick={() => setActiveTab('offres')}
                                className={`
                                flex-1 py-3 rounded-full text-sm font-bold transition-all text-center
                                ${activeTab === 'offres' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-500'}
                            `}
                            >
                                Offres de VIE
                            </button>
                            <button
                                onClick={() => setActiveTab('entreprises')}
                                className={`
                                flex-1 py-3 rounded-full text-sm font-bold transition-all text-center
                                ${activeTab === 'entreprises' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-500'}
                            `}
                            >
                                Entreprises
                            </button>
                        </div>
                    </div>

                    {/* ========== DESKTOP HEADER ========== */}
                    <div className="hidden md:flex relative items-center justify-center mb-10 pt-4 lg:pt-10">
                        <h1 className="lg:absolute lg:left-0 text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3 mb-6 lg:mb-0">
                            <Heart className="fill-blue-500 text-blue-500" />
                            Mes Favoris
                        </h1>

                        <div className="bg-white p-1 rounded-full shadow-sm border border-gray-200 inline-flex">
                            <button
                                onClick={() => setActiveTab('offres')}
                                className={`
                                px-8 py-3 rounded-full text-sm font-bold transition-all
                                ${activeTab === 'offres' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-500 hover:text-slate-900'}
                            `}
                            >
                                Offres de VIE
                            </button>
                            <button
                                onClick={() => setActiveTab('entreprises')}
                                className={`
                                px-8 py-3 rounded-full text-sm font-bold transition-all
                                ${activeTab === 'entreprises' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-500 hover:text-slate-900'}
                            `}
                            >
                                Entreprises
                            </button>
                        </div>


                    </div>


                    {/* Content Area */}
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {activeTab === 'offres' && (
                            <div className="grid grid-cols-1 lg:grid-cols-13 gap-8 items-start">
                                {/* Left Column: List */}
                                <div className="lg:col-span-5 flex flex-col gap-4">
                                    {loadingOffers ? (
                                        <div className="flex justify-center p-10"><Loader2 className="animate-spin text-blue-500" /></div>
                                    ) : favoriteOffers.length > 0 ? (
                                        favoriteOffers.map((offer) => (
                                            <OfferCard
                                                key={offer.id}
                                                offer={offer}
                                                isSelected={selectedOffer?.id === offer.id}
                                                onClick={handleOfferClick}
                                                isFavorite={true}
                                                onToggleFavorite={handleRemoveOfferFavorite}
                                            />
                                        ))
                                    ) : (
                                        <div className="p-8 text-center text-slate-500 bg-white rounded-xl border border-dashed">
                                            Aucune offre favorite pour le moment.
                                        </div>
                                    )}
                                </div>

                                {/* Right Column: Detail View (Desktop only) */}
                                <div className="hidden lg:block lg:col-span-8 sticky top-24">
                                    <OfferDetailView
                                        offer={selectedOffer}
                                        isFavorite={true}
                                        onToggleFavorite={handleRemoveOfferFavorite}
                                    />
                                </div>
                            </div>
                        )}

                        {activeTab === 'entreprises' && (
                            <div className="flex flex-col gap-4 w-full">
                                {loadingCompanies ? (
                                    <div className="flex justify-center py-10">
                                        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                                    </div>
                                ) : favoriteCompanies.length > 0 ? (
                                    <>

                                        {/* Mobile Company Cards */}
                                        <div className="md:hidden space-y-3">
                                            {favoriteCompanies.map((company, idx) => (
                                                <div
                                                    key={`${company.name}-${idx}`}
                                                    className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm cursor-pointer hover:shadow-md hover:border-blue-100 transition-all"
                                                    onClick={() => { setSelectedCompanyForModal(company.name); setShowInsightsModal(true); }}
                                                >
                                                    {/* Top section: Avatar + Name + Heart */}
                                                    <div className="flex items-start gap-3">
                                                        {/* Avatar */}
                                                        <div className={`w-10 h-10 rounded-xl ${getAvatarStyle(company.name).bg} flex items-center justify-center flex-shrink-0`}>
                                                            <span className={`${getAvatarStyle(company.name).text} font-bold text-sm`}>
                                                                {company.name ? company.name.charAt(0).toUpperCase() : '?'}
                                                            </span>
                                                        </div>

                                                        {/* Name & Context */}
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="font-bold text-slate-900 text-sm leading-tight truncate">
                                                                {company.name}
                                                            </h3>
                                                            <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide mt-0.5">
                                                                Global
                                                            </p>
                                                        </div>

                                                        {/* Favorite Button */}
                                                        <button
                                                            onClick={() => toggleFavoriteCompany(company.name)}
                                                            className="p-2 -mr-1 -mt-1"
                                                        >
                                                            <Heart
                                                                size={20}
                                                                className="text-blue-500 fill-blue-500"
                                                            />
                                                        </button>
                                                    </div>

                                                    {/* Separator line */}
                                                    <div className="border-t border-slate-100 my-3" />

                                                    {/* Bottom section: Offres disponibles + Actifs/Total */}
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-slate-400">Offres disponibles</span>
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex flex-col items-center">
                                                                <span className="text-[10px] text-slate-400 uppercase">Actifs</span>
                                                                <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg font-bold text-base">{company.active}</span>
                                                            </div>
                                                            <div className="flex flex-col items-center">
                                                                <span className="text-[10px] text-slate-400 uppercase">Total</span>
                                                                <span className="px-3 py-1 bg-violet-400/10 text-violet-600 rounded-lg font-bold text-base">{company.total}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Desktop Company Cards - using existing CompanyCard */}
                                        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {favoriteCompanies.map((company, idx) => (
                                                <CompanyCard
                                                    key={`${company.name}-${idx}`}
                                                    company={company}
                                                    isFavorite={true}
                                                    onToggleFavorite={toggleFavoriteCompany}
                                                    contextLabel="Global"
                                                    onClick={(c) => { setSelectedCompanyForModal(c.name); setShowInsightsModal(true); }}
                                                />
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-200">
                                        <p className="text-slate-400 font-medium">Vous n'avez pas encore ajouté d'entreprises favorites.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile Offer Drawer */}
                <MobileOfferDrawer
                    isOpen={mobileDrawerOpen}
                    onClose={() => setMobileDrawerOpen(false)}
                    offer={selectedOffer}
                    isFavorite={true}
                    onToggleFavorite={handleRemoveOfferFavorite}
                />
                {/* Auth Required Modal */}
                {showAuthModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                            onClick={() => navigate('/')}
                        />

                        {/* Modal Content */}
                        <div className="relative bg-white rounded-[32px] w-full max-w-md p-8 shadow-2xl transform transition-all scale-100 animate-in fade-in zoom-in-95 duration-200">

                            {/* Close Button - Redirects to Home or previous */}
                            <button
                                onClick={() => navigate('/')}
                                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
                            >
                                <X size={24} />
                            </button>

                            {/* Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-20 h-20 bg-blue-50 rounded-3xl flex items-center justify-center transform rotate-3 shadow-sm border border-blue-100">
                                    <UserPlus className="w-10 h-10 text-blue-600" />
                                </div>
                            </div>

                            {/* Text Content */}
                            <div className="text-center mb-8">
                                <h3 className="text-2xl font-black text-slate-900 mb-3 tracking-tight">
                                    Connectez-vous
                                </h3>
                                <p className="text-slate-500 font-medium leading-relaxed mb-6">
                                    Pour accéder à vos favoris, vous devez être connecté
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-3">
                                <button
                                    onClick={() => navigate('/onboarding')}
                                    className="w-full py-4 rounded-xl bg-blue-600 text-white font-bold text-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
                                >
                                    Créer un compte gratuit
                                </button>
                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-full py-3 rounded-xl text-slate-600 font-bold hover:bg-slate-50 transition-colors"
                                >
                                    Déjà un compte ? Se connecter
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Company Insights Modal */}
                <CompanyInsightsModal
                    companyName={selectedCompanyForModal}
                    isOpen={showInsightsModal}
                    onClose={() => setShowInsightsModal(false)}
                    isPremium={isPremium}
                />
            </ErrorBoundary>
        </div>
    );
};

export default FavoritesPage;
